import React, { useEffect, useState } from 'react';
import style from '../../pages/Automations.scss.json';
import { Server } from 'lincd-server-utils/lib/utils/Server';
import { packageName } from '../../package';
import { automation3 } from '../../data/automations';
import { AdsAutomationHeader, AdsKeepRunningTable, AdsToPauseTable } from './AdsReusableComponents';

type AdState = {
  uri: string;
  shortName: string;
  quoraId: string;
};

type AdsState = {
  adsToPause: AdState[];
  adsKeepRunning: AdState[];
  adsKeepRunningReasons: Record<
    string,
    {
      rule: string;
      message: string;
    }[]
  >;
};

interface RunConvertingAdsProps {
  onRunConvertingAds?: () => Promise<AdsState | null>;
  onPauseAds?: (uris: string[]) => Promise<boolean>;
  onGetLastExecuted?: () => Promise<{ currentTime: string; lastExecuted: string } | null>;
}

export function RunConvertingAds({
  onRunConvertingAds,
  onPauseAds,
  onGetLastExecuted,
}: RunConvertingAdsProps) {
  const [ads, setAds] = useState<AdsState | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastExecuted, setLastExecuted] = useState<{ currentTime: string; lastExecuted: string } | null>(null);
  const [selectedAds, setSelectedAds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState<boolean>(true);

  useEffect(() => {
    handleGetLastExecuted();
  }, []);

  const handleRunConvertingAds = async () => {
    setLoading(true);
    try {
      const response = onRunConvertingAds
        ? await onRunConvertingAds()
        : await Server.call(packageName, 'runConvertingAds');
      setAds(response);

      if (response?.adsToPause && selectAll) {
        const allUris = new Set<string>(response.adsToPause.map((ad: AdState) => ad.uri));
        setSelectedAds(allUris);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGetLastExecuted = async () => {
    try {
      const response = onGetLastExecuted
        ? await onGetLastExecuted()
        : await Server.call(packageName, 'getLastExecuted');
      setLastExecuted(response);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCheckboxChange = (uri: string) => {
    setSelectedAds((prevSelectedAds) => {
      const newSelectedAds = new Set(prevSelectedAds);
      if (newSelectedAds.has(uri)) {
        newSelectedAds.delete(uri);
      } else {
        newSelectedAds.add(uri);
      }
      setSelectAll(newSelectedAds.size === ads?.adsToPause.length);
      return newSelectedAds;
    });
  };

  const handleSelectAllChange = () => {
    setSelectAll(!selectAll);
    if (!selectAll && ads?.adsToPause) {
      const allUris = new Set<string>(ads.adsToPause.map((ad) => ad.uri));
      setSelectedAds(allUris);
    } else {
      setSelectedAds(new Set<string>());
    }
  };

  const handlePauseAds = async () => {
    setLoading(true);
    try {
      const uris = Array.from(selectedAds);
      const response = onPauseAds
        ? await onPauseAds(uris)
        : await Server.call(packageName, 'runPausedAds', uris);

      if (!response) {
        alert('Paused Ads Failed!');
        return;
      }

      setAds((prevAds) => {
        if (!prevAds) return prevAds;
        const newAdsToPause = prevAds.adsToPause.filter((ad) => !uris.includes(ad.uri));
        return { ...prevAds, adsToPause: newAdsToPause };
      });
      setSelectedAds(new Set<string>());
      handleGetLastExecuted();
      alert('Paused Ads Successfully!');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const infoRules = [
    '2+ sales with a positive gross P&L from the same weekday the prior week.',
    '1+ sale after the time of execution from 2 or more of the last 4 days (this accounts for afternoon/nighttime user behaviour variance).',
    '1+ sale yesterday after the time of execution.',
    '2+ sales from any of the same past 4 week days.',
    '1+ sale from any of the same past 4 week days after the time of execution.',
    '2+ sales with a positive gross P&L on the same day'
  ];

  return (
    <>
      <AdsAutomationHeader
        automation={automation3}
        lastExecuted={lastExecuted}
        onRetrieve={handleRunConvertingAds}
        loading={loading}
        infoRules={infoRules}
        retrieveButtonLabel="Retrieve Ads"
      />

      {ads?.adsKeepRunning && ads.adsKeepRunning.length > 0 && (
        <AdsKeepRunningTable
          ads={ads.adsKeepRunning}
          adsKeepRunningReasons={ads.adsKeepRunningReasons}
        />
      )}

      {ads?.adsToPause && ads.adsToPause.length > 0 && (
        <>
          <AdsToPauseTable
            ads={ads.adsToPause}
            selectedAds={selectedAds}
            onCheckboxChange={handleCheckboxChange}
            onSelectAllChange={handleSelectAllChange}
            selectAll={selectAll}
          />
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              className={style.btn}
              onClick={handlePauseAds}
              disabled={loading || selectedAds.size === 0}
            >
              {loading ? 'Pausing...' : `Pause ${selectedAds.size > 0 ? selectedAds.size : ''} Ads`}
            </button>
          </div>
        </>
      )}
    </>
  );
}
